import { Fragment, useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Download, Eye, Loader2, LogOut, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAdmin } from "@/context/AdminAuth";
import { http, errMsg } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = ["new", "in_review", "scheduled", "concluded"];
const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

function Leads({ onChange }) {
  const [leads, setLeads] = useState(null);
  const [type, setType] = useState("");
  const [view, setView] = useState(null);
  const load = useCallback(() => {
    http.get("/admin/leads", { params: type ? { type } : {} }).then((r) => setLeads(Array.isArray(r.data) ? r.data : [])).catch(() => setLeads([]));
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await http.patch(`/admin/leads/${id}`, { status });
    setLeads((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
    onChange();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    await http.delete(`/admin/leads/${id}`);
    load(); onChange();
  };
  const exportCsv = async () => {
    const r = await http.get("/admin/leads/export", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "immistop-leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2">
          {[["", "All"], ["contact", "Consultations"], ["eligibility", "Eligibility"]].map(([v, l]) => (
            <button key={l} type="button" onClick={() => setType(v)} data-testid={`leads-filter-${l.toLowerCase()}`} className={`border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${type === v ? "border-navy bg-navy text-ivory" : "border-navy/20 text-navy"}`}>{l}</button>
          ))}
        </div>
        <button type="button" onClick={exportCsv} className="btn-gold !py-3" data-testid="leads-export-btn"><Download className="h-4 w-4" /><span>Export CSV</span></button>
      </div>
      <div className="mt-6 overflow-x-auto border border-navy/10 bg-white">
        <table className="w-full min-w-[860px] text-left text-[14px]" data-testid="leads-table">
          <thead className="border-b border-navy/10 bg-ivory-100 text-[11px] uppercase tracking-[0.18em] text-charcoal-soft">
            <tr>{["Received", "Name", "Contact", "Type", "Interest", "Status", ""].map((h) => <th key={h} className="px-5 py-4 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {leads === null ? <tr><td colSpan={7} className="px-5 py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-gold-deep" /></td></tr> :
              leads.length === 0 ? <tr><td colSpan={7} className="px-5 py-10 text-center text-charcoal-soft" data-testid="leads-empty">No leads yet.</td></tr> :
              leads.map((l) => (
                <tr key={l.id} className="border-b border-navy/5 hover:bg-ivory" data-testid={`lead-row-${l.id}`}>
                  <td className="whitespace-nowrap px-5 py-4 text-charcoal-soft">{fmt(l.created_at)}</td>
                  <td className="px-5 py-4 font-medium text-navy">{l.name}</td>
                  <td className="px-5 py-4 text-charcoal-soft">{l.email}<br />{l.country_code} {l.phone}</td>
                  <td className="px-5 py-4 capitalize">{l.type === "contact" ? "Consultation" : "Eligibility"}</td>
                  <td className="px-5 py-4 text-charcoal-soft">{l.destination}<br />{l.service}</td>
                  <td className="px-5 py-4">
                    <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} className="border border-navy/20 bg-white px-2 py-1.5 text-[13px]" data-testid={`lead-status-${l.id}`} aria-label="Lead status">
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                  <td className="whitespace-nowrap px-5 py-4">
                    <button type="button" onClick={() => setView(l)} className="p-2 text-navy hover:text-gold-deep" aria-label="View lead" data-testid={`lead-view-${l.id}`}><Eye className="h-4 w-4" /></button>
                    <button type="button" onClick={() => remove(l.id)} className="p-2 text-navy hover:text-destructive" aria-label="Delete lead" data-testid={`lead-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-lg rounded-none" data-testid="lead-detail-dialog">
          <DialogHeader><DialogTitle className="font-serif text-3xl font-normal text-navy">{view?.name}</DialogTitle></DialogHeader>
          {view && (
            <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-3 text-[14px]">
              {[["Email", view.email], ["Phone", `${view.country_code} ${view.phone}`], ["Destination", view.destination], ["Service", view.service], ["Mode", view.mode], ["Office", view.office], ["Preferred", [view.preferred_date, view.preferred_time].filter(Boolean).join(" · ")], ...Object.entries(view.answers || {}), ["Message", view.message], ["Received", fmt(view.created_at)]].filter(([, v]) => v).map(([k, v]) => (
                <Fragment key={k}><dt className="text-[11px] uppercase tracking-[0.18em] text-charcoal-soft">{k}</dt><dd className="text-navy">{v}</dd></Fragment>
              ))}
            </dl>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EMPTY = { title: "", slug: "", excerpt: "", category: "country", tag: "", cover: "/img/london-aerial.jpg", body: "", read_time: 5, published: true };

function PostEditor({ post, onClose, onSaved }) {
  const [f, setF] = useState(post || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : k === "read_time" ? Number(e.target.value) : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { title: f.title, slug: f.slug, excerpt: f.excerpt, category: f.category, tag: f.tag, cover: f.cover, body: f.body, read_time: f.read_time, published: f.published };
      if (post?.id) await http.put(`/admin/posts/${post.id}`, body); else await http.post("/admin/posts", body);
      toast.success("Article saved");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const inp = "mt-1 w-full border border-navy/20 bg-white px-3 py-2.5 text-[14px] focus:border-gold focus:outline-none";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-none" data-lenis-prevent data-testid="post-editor-dialog">
        <DialogHeader><DialogTitle className="font-serif text-3xl font-normal text-navy">{post?.id ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <label className="sm:col-span-2"><span className="label-lux">Title</span><input required className={inp} value={f.title} onChange={set("title")} data-testid="post-title-input" /></label>
          <label><span className="label-lux">Category</span><select className={inp} value={f.category} onChange={set("category")} data-testid="post-category-select"><option value="country">Country</option><option value="service">Service</option><option value="policy">Policy update</option></select></label>
          <label><span className="label-lux">Tag</span><input className={inp} value={f.tag} onChange={set("tag")} placeholder="e.g. Canada" data-testid="post-tag-input" /></label>
          <label><span className="label-lux">Cover image URL</span><input className={inp} value={f.cover} onChange={set("cover")} data-testid="post-cover-input" /></label>
          <label><span className="label-lux">Read time (min)</span><input type="number" min={1} className={inp} value={f.read_time} onChange={set("read_time")} data-testid="post-readtime-input" /></label>
          <label className="sm:col-span-2"><span className="label-lux">Excerpt</span><textarea rows={2} className={inp} value={f.excerpt} onChange={set("excerpt")} data-testid="post-excerpt-input" /></label>
          <label className="sm:col-span-2"><span className="label-lux">Body — blank line between paragraphs, "## " for headings</span><textarea rows={10} className={inp} value={f.body} onChange={set("body")} data-testid="post-body-input" /></label>
          <label className="flex items-center gap-3 text-[14px]"><input type="checkbox" checked={f.published} onChange={set("published")} data-testid="post-published-checkbox" />Published</label>
          <button type="submit" disabled={busy} className="btn-navy sm:col-span-2" data-testid="post-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save article"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Posts({ onChange }) {
  const [posts, setPosts] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => { http.get("/admin/posts").then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => setPosts([])); }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await http.delete(`/admin/posts/${id}`); load(); onChange();
  };
  return (
    <div>
      <div className="flex justify-end"><button type="button" onClick={() => setEdit({})} className="btn-gold !py-3" data-testid="post-new-btn"><Plus className="h-4 w-4" /><span>New article</span></button></div>
      <ul className="mt-6 divide-y divide-navy/10 border border-navy/10 bg-white" data-testid="posts-list">
        {posts.map((p) => (
          <li key={p.id} className="flex items-center gap-5 p-4" data-testid={`post-row-${p.slug}`}>
            <img src={p.cover} alt="" className="h-14 w-20 shrink-0 object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-serif text-xl text-navy">{p.title}</p>
              <p className="text-[12px] uppercase tracking-[0.15em] text-charcoal-soft">{p.category} · {p.tag} · {p.published ? "Published" : "Draft"}</p>
            </div>
            <button type="button" onClick={() => setEdit(p)} className="p-2 text-navy hover:text-gold-deep" aria-label="Edit article" data-testid={`post-edit-${p.slug}`}><Pencil className="h-4 w-4" /></button>
            <button type="button" onClick={() => remove(p.id)} className="p-2 text-navy hover:text-destructive" aria-label="Delete article" data-testid={`post-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
      {edit && <PostEditor post={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); onChange(); }} />}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAdmin();
  const [stats, setStats] = useState(null);
  const loadStats = useCallback(() => { http.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user) loadStats(); }, [user, loadStats]);
  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-ivory"><Loader2 className="h-6 w-6 animate-spin text-gold-deep" /></div>;
  if (user === false) return <Navigate to="/admin" replace />;
  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <header className="bg-navy text-ivory">
        <div className="container-lux flex items-center justify-between py-4">
          <div className="flex items-center gap-4"><img src="/brand/logo.webp" alt="Immistop crest" className="h-12 w-auto" /><span className="font-serif text-2xl">Client Desk</span></div>
          <div className="flex items-center gap-5 text-[13px]"><span className="hidden text-ivory/75 sm:inline" data-testid="admin-user-email">{user.email}</span><button type="button" onClick={logout} className="flex items-center gap-2 text-gold" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" />Sign out</button></div>
        </div>
      </header>
      <main className="container-lux py-10">
        <div className="grid grid-cols-2 gap-px bg-navy/10 lg:grid-cols-5">
          {[["Total leads", stats?.total], ["New", stats?.new], ["Consultations", stats?.contact], ["Eligibility", stats?.eligibility], ["Articles", stats?.posts]].map(([l, v]) => (
            <div key={l} className="bg-white p-6" data-testid={`admin-stat-${l.toLowerCase().replace(" ", "-")}`}><p className="font-serif text-4xl text-navy">{v ?? "—"}</p><p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-charcoal-soft">{l}</p></div>
          ))}
        </div>
        <Tabs defaultValue="leads" className="mt-10">
          <TabsList className="rounded-none bg-ivory-200">
            <TabsTrigger value="leads" className="rounded-none px-6" data-testid="admin-tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="posts" className="rounded-none px-6" data-testid="admin-tab-posts">Insights</TabsTrigger>
          </TabsList>
          <TabsContent value="leads" className="mt-6"><Leads onChange={loadStats} /></TabsContent>
          <TabsContent value="posts" className="mt-6"><Posts onChange={loadStats} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
